'use client';

import { useMemo, useState } from 'react';
import { TransitionStatus } from 'react-transition-group';
import { useStore, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useHotKey } from '@nx-frontend/shared/hooks';
import { getBgClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import { ModalTitleSize, PortalBgType, PortalTextType } from '@nx-frontend/shared/types';
import { ModalLogin } from './modal-login';
import { $modalPositions, $ModalStore } from './modal-store';

export enum UiModalContent {
  LOGIN = 'login',
}

export const titleSizeMap = {
  [ModalTitleSize.XL]: 'text-xl',
  [ModalTitleSize.LG]: 'text-lg',
  [ModalTitleSize.MD]: 'text-md',
  [ModalTitleSize.SM]: 'text-sm',
};

interface UiModalProps {
  id?: number;
  templatePopup?: UiModalContent | string | null;
  closePopUp: () => void;
  title?: string | null;
  children?: React.ReactNode;
  showCloseButton?: boolean | null | undefined;
  closeButtonSize?: number; // on set custom size, need custom css styles in project
  titleSize?: ModalTitleSize;
  isFullScreen?: boolean;
  transitionStatus?: TransitionStatus;
  usePadding?: boolean;
}

export const ModalWrapper = ({
  id,
  title,
  templatePopup,
  closePopUp,
  showCloseButton,
  closeButtonSize = 24,
  titleSize = ModalTitleSize.XL,
  children,
  transitionStatus,
  isFullScreen,
  usePadding = true,
}: UiModalProps) => {
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const modalPosition = useStore($modalPositions);
  const [modalStore] = useUnit([$ModalStore]);

  const [mouseDownInsidePopup, setMouseDownInsidePopup] = useState(false);
  const closeSize = closeButtonSize + 'px';

  const currentModal = id ? modalStore.find((modal) => modal.id === id) : modalStore[modalPosition.length - 1]; // For publish popup
  const currentPosition = modalPosition[modalPosition.length - 1];
  const modalContainerStyles = useMemo(() => {
    return currentModal?.modalContainerStyles ?? '';
  }, [currentModal]);

  const themeColors =
    currentModal && currentModal.payload && 'themeColors' in currentModal.payload
      ? currentModal.payload.themeColors
      : undefined;

  useHotKey('Escape', function onEnter() {
    if (currentModal && currentModal.escapeToClose !== false) {
      closePopUp();
    }
  });

  const classNamesTransition: Record<string, boolean> = {};
  if (!transitionStatus) {
    transitionStatus = 'entered';
  }
  if (transitionStatus === 'entering' || transitionStatus === 'entered') {
    classNamesTransition['scale-100'] = true;
  } else if (transitionStatus === 'exiting' || transitionStatus === 'exited') {
    classNamesTransition['scale-90'] = true;
  }

  const backgroundSize = isFullScreen ? 'w-full h-full overflow-auto' : '';
  const backgroundPadding = usePadding ? 'p-5' : '';

  const backgroundColor = !themeColors && modalContainerStyles.indexOf('bg-') === -1 ? 'bg-white' : '';
  const modalGradient = currentModal?.modalGradient;
  const disabledClose = currentModal?.disabledClose;

  const styles = {
    ...(currentPosition ? currentPosition : {}),
    ...(modalGradient ? { background: modalGradient } : {}),
  };

  const zIndexClass = currentModal ? (currentModal.zIndex ? `z-[${currentModal.zIndex}]` : 'z-40') : 'z-40';

  const handleModalMouseDown = () => {
    setMouseDownInsidePopup(false);
  };
  const handlePopupMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMouseDownInsidePopup(true);
  };
  const handleModalMouseUp = () => {
    if (!mouseDownInsidePopup) {
      if (!disabledClose) {
        closePopUp();
      }
    }
  };

  return (
    <div
      id="ModalContainer"
      onMouseDown={handleModalMouseDown}
      onMouseUp={handleModalMouseUp}
      className={classNames('fixed inset-0 flex items-center justify-center', zIndexClass)}
    >
      <div
        className={classNames(
          `rounded-lg shadow-lg z-50 transition-transform duration-200 ease-out will-change-transform`,
          backgroundSize,
          backgroundPadding,
          themeColors && getBgClassNames(themeColors, PortalBgType.SURFACE_INNER),
          themeColors && getTextClassNames(themeColors, PortalTextType.SURFACE),
          {
            absolute: !!modalPosition,
            ...classNamesTransition,
          },
          modalContainerStyles,
          backgroundColor
        )}
        onMouseDown={handlePopupMouseDown}
        onClick={handleContentClick}
        {...(styles ? { style: styles } : null)}
      >
        {title || showCloseButton ? (
          <div className="flex justify-between items-start mb-2">
            {title && <h2 className={'font-semibold ' + titleSizeMap[titleSize]}>{title}</h2>}
            {showCloseButton !== false && IconsMap.CLOSE && (
              <div
                className={classNames('cursor-pointer mb-2 relative z-10', 'text-gray-500')}
                style={{ width: closeSize, height: closeSize }}
                onClick={closePopUp}
              >
                <Icon icon={IconsMap.CLOSE} />
              </div>
            )}
          </div>
        ) : null}
        {templatePopup === UiModalContent.LOGIN && <ModalLogin />}
        {children}
      </div>
    </div>
  );
};
